import { type PropsWithChildren, Children } from 'react'
import { Box, type SvgIconProps } from '@mui/material'

export type StackIconsProps = PropsWithChildren<SvgIconProps> & {
  /**
   * Choose the variant on how stacked icons should behave, see more info in the documentation of storybook.
   *
   *  Documentation:
   * - [StackedIcons behavior](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/dhl-ui-icons--docs#stacked-icons)
   *
   * @default 'top-right'
   */

  variant?: 'full' | 'top-right' | 'centered'

  /**
   * Give the icon a disabled state, icons will be rendered in a gray color.
   *
   * @default false
   */
  disabled?: boolean
}

export const StackIcons = ({
  children,
  className,
  fontSize,
  variant = 'top-right',
  disabled = false,
  color,
  ...props
}: StackIconsProps) => (
  <Box
    className={[
      'DhlSvgStack-root',
      `DhlSvgStack-${variant}`,
      ...(disabled ? [`DhlSvgStack-disabled`] : []),
      ...(className ? [className] : []),
    ].join(' ')}
  >
    {Children.map(children, (child) => {
      // Pass on the props from the parent StackedIcon to the children, so that devs can set
      // the size, color, etc. from the parent and prevents duplicated code
      if (child && typeof child === 'object' && 'props' in child) {
        return {
          ...child,
          props: {
            color,
            disabled,
            fontSize,
            ...(child?.props || {}),
            ...props,
            sx: {
              ...((child?.props as SvgIconProps)?.sx || {}),
              ...(props.sx || {}),
            },
          },
        }
      }

      return child
    })}
  </Box>
)
